import { selectIsGuest } from 'redux/db/reducers';
import type { DsEditBlockReason } from 'redux/db/util';
import {
	showAlertModal,
	showConfirmModal,
	showPromptModal,
	setActiveSceneId,
	setPlanPricingModalVisible,
	showDsShowcaseModal,
	setHistoricalDsVersionNumber,
} from 'redux/mode/actions';
import type { DsShowcaseModalOptions } from 'redux/mode/reducers';
import type {
	AlertModalOptions,
	ConfirmModalOptions,
	PromptModalOptions,
} from 'redux/mode/reducers';
import store from 'redux/store';
import { navigate } from 'utils';
import { trackEconomyAction, trackDesignSystemAction } from 'analytics';
import { profile } from 'routes';
import forge from 'apis/forge';
import { toast } from 'react-toastify';

export const displayPromptModal = (options: PromptModalOptions) => {
	store.dispatch(showPromptModal(options));
};

export const displayConfirmModal = (options: ConfirmModalOptions) => {
	store.dispatch(showConfirmModal(options));
};

export const displayAlertModal = (options: AlertModalOptions) => {
	store.dispatch(showAlertModal(options));
};

export const displayPlanPricingModal = () => {
	store.dispatch(setPlanPricingModalVisible(true));
};

export const displayDsShowcaseModal = (options: DsShowcaseModalOptions) => {
	store.dispatch(showDsShowcaseModal(options));
};

export const displayViewOnlyModal = () => {
	if (selectIsGuest(store.getState().db)) {
		displayPlanPricingModal();
		return;
	}

	displayAlertModal({
		title: "You're in view-only mode",
		description:
			"You can look around, but you'll need edit access to make changes. Ask the owner for edit access to start designing.",
		submitLabel: 'OK',
		onSubmit: () => {},
	});
};

export const displayUpgradeModal = (featureName: string) => {
	trackEconomyAction('feature_upgrade_modal_shown');

	displayConfirmModal({
		title: 'Upgrade Required',
		description: `${featureName} is available for Pro and Max subscribers. Upgrade to unlock this feature and more.`,
		cancelLabel: 'Close',
		submitLabel: 'Upgrade',
		onSubmit: () => {
			trackEconomyAction('upgrade_clicked');
			store.dispatch(setActiveSceneId(null));
			navigate(profile());
		},
	});
};

export const displayPublishDsModal = (dsId: string, dsTitle: string) => {
	displayConfirmModal({
		title: `Publish ${dsTitle}?`,
		description: 'The current draft will become the latest published version.',
		cancelLabel: 'Cancel',
		submitLabel: 'Publish',
		onSubmit: async () => {
			try {
				const response = await forge.publishDsVersion(dsId);
				trackDesignSystemAction('publish');
				toast.success(`Design system published as v${response.versionNumber}`);
			} catch (error) {
				toast.error('Failed to publish design system');
			}
		},
	});
};

// Shows the appropriate modal for a given DS edit block reason
export const displayDsReadOnlyModal = (reason: DsEditBlockReason) => {
	switch (reason) {
		case 'guest':
			displayPlanPricingModal();
			return;
		case 'historical': {
			const version = store.getState().mode.historicalDsVersionNumber;
			store.dispatch(
				showConfirmModal({
					title: `Viewing version ${version}`,
					description:
						"You're viewing a historical version. Switch to the current version to make edits.",
					cancelLabel: 'Cancel',
					submitLabel: 'Switch to current',
					onSubmit: () => {
						store.dispatch(setHistoricalDsVersionNumber(null));
					},
				})
			);
			return;
		}
		case 'read-only':
			displayAlertModal({
				title: "You can't edit this design system",
				description: 'You do not have edit access to this design system.',
				submitLabel: 'OK',
				onSubmit: () => {},
			});
			return;
	}
};
